import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Baby, Users, Heart, Mail } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import { SectionHeading } from "@/components/site/SectionHeading";
import { Sunflower, SunflowerMini } from "@/components/site/Sunflower";

const categories = [
  {
    icon: Baby,
    title: "For Children",
    desc: "Direct support for Autism and ADHD — in person in Dubai or online.",
    items: [
      {
        name: "1:1 Sessions",
        body: "Personalised sessions focused on regulation, communication, and skill-building for your child's specific profile.",
      },
      {
        name: "Emotional Regulation Programs",
        body: "A structured 8-week program to help children identify, name, and move through big feelings.",
      },
      {
        name: "Social Communication Support",
        body: "Scripts, role-play, and generalisation strategies for playdates, school, and family life.",
      },
    ],
  },
  {
    icon: Users,
    title: "Group Classes",
    desc: "Small online groups for peer connection and skill transfer.",
    items: [
      {
        name: "Online Small Group Sessions",
        body: "Groups of 3–5 children led by Nada, grouped by age and readiness.",
      },
      {
        name: "Emotional Intelligence Program",
        body: "A signature group curriculum building self-awareness, empathy, and regulation week by week.",
      },
      {
        name: "Skill-Building Workshops",
        body: "Short-form themed workshops: transitions, sensory play, friendships, morning routines.",
      },
    ],
  },
  {
    icon: Heart,
    title: "Parent Support",
    desc: "You're not alone. Parenting a neurodivergent child is skill-based — and teachable.",
    items: [
      {
        name: "1:1 Parent Coaching",
        body: "Private coaching where we look at your actual week and design the next small change.",
      },
      {
        name: "Home Strategies",
        body: "Sensory setup, mealtime, meltdowns, sleep — we tackle what's actually hard right now.",
      },
      {
        name: "Routine Building",
        body: "A step-by-step system to design routines that hold even on hard days.",
      },
    ],
  },
];

export default function Services() {
  const { hash } = useLocation();
  useEffect(() => {
    if (hash === "#book") {
      const el = document.getElementById("book");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [hash]);

  return (
    <div data-testid="services-page">
      <section className="section-pad relative overflow-hidden petal-bg">
        <div className="absolute -top-14 -right-14 opacity-35 pointer-events-none hidden md:block">
          <Sunflower size={240} className="animate-slowspin" />
        </div>
        <div className="relative grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <span className="inline-flex items-center gap-2 text-xs tracking-[0.22em] uppercase text-brand-leafDeep bg-brand-honey/50 border border-brand-sun/40 rounded-full px-4 py-1.5 mb-6 w-fit">
              <SunflowerMini size={18} />
              Services
            </span>
            <h1 className="font-heading font-normal text-[2.75rem] sm:text-5xl lg:text-[4.25rem] leading-[1.02] tracking-tight">
              Support for the whole system
              <br />
              around the <span className="sunray-underline">child</span>.
            </h1>
            <p className="mt-7 text-brand-muted text-base md:text-lg leading-relaxed font-body max-w-xl">
              Every engagement starts with a 30-minute discovery session. From
              there, Nada recommends the right path — 1:1, group, parent
              coaching, or a blend.
            </p>
          </div>
          <div className="lg:col-span-5">
            <Reveal>
              <div className="relative">
                <div className="rounded-[32px] overflow-hidden border-4 border-brand-sun/40 bg-gradient-to-br from-brand-honey/30 to-brand-leafSoft/15 shadow-earthyLg aspect-[4/5] grid place-items-center p-8">
                  <img
                    src="/covers/feelings-are-friends.jpg"
                    alt="Feelings Are Friends — companion book used in many sessions"
                    className="max-h-full max-w-full object-contain drop-shadow-[0_18px_40px_rgba(44,36,22,0.22)]"
                  />
                </div>
                <span className="absolute -top-3 left-6 bg-brand-sun text-brand-ink text-[11px] uppercase tracking-[0.22em] font-semibold px-4 py-1.5 rounded-full shadow-sunny">
                  Used in sessions
                </span>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {categories.map((cat, idx) => {
        const toneBg = idx % 2 === 0 ? "bg-brand-ivory" : "";
        const iconTones = [
          "bg-brand-sun/25 text-brand-sunDeep",
          "bg-brand-leaf/20 text-brand-leafDeep",
          "bg-brand-terracotta/20 text-brand-rust",
        ];
        return (
        <section
          key={cat.title}
          className={`section-pad ${toneBg} relative overflow-hidden`}
        >
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <div className={`h-14 w-14 rounded-2xl ${iconTones[idx]} grid place-items-center mb-6`}>
                <cat.icon size={24} />
              </div>
              <p className="font-brand text-3xl text-brand-sun mb-4">
                0{idx + 1}
              </p>
              <h2 className="font-heading font-normal text-3xl sm:text-4xl lg:text-5xl leading-tight">
                {cat.title}
              </h2>
              <p className="mt-5 text-brand-muted leading-relaxed font-body">
                {cat.desc}
              </p>
            </div>
            <div className="lg:col-span-8 grid gap-5">
              {cat.items.map((item, i) => (
                <Reveal key={item.name} delay={i * 0.08}>
                  <div
                    data-testid={`service-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="bg-white rounded-[24px] p-8 border-2 border-brand-line hover:border-brand-sun/50 flex items-start justify-between gap-6 hover:-translate-y-0.5 hover:shadow-earthy transition-all"
                  >
                    <div>
                      <h3 className="font-heading text-xl mb-2">{item.name}</h3>
                      <p className="text-sm text-brand-muted leading-relaxed font-body">
                        {item.body}
                      </p>
                    </div>
                    <Link
                      data-testid={`service-book-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      to="/contact"
                      className="shrink-0 rounded-full bg-brand-sun hover:bg-brand-sunDeep text-brand-ink px-5 py-2.5 text-xs font-medium inline-flex items-center gap-2 transition-colors"
                    >
                      Contact <ArrowRight size={12} />
                    </Link>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      );})}

      {/* CONTACT CTA */}
      <section id="book" className="section-pad relative overflow-hidden bg-brand-ivory">
        <div className="absolute -bottom-10 -right-10 opacity-25 pointer-events-none">
          <Sunflower size={220} showLeaves className="animate-slowspin" />
        </div>
        <div className="relative">
          <SectionHeading
            eyebrow="Get in touch"
            title="Ready to take the next step?"
            intro="Reach out and Nada will personally guide you to the right starting point — children's sessions, parent coaching, or group classes."
          />
          <div className="mt-12 grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-[28px] border-2 border-brand-line hover:border-brand-sun/50 p-8 transition-colors">
              <div className="h-12 w-12 rounded-2xl bg-brand-sun/25 text-brand-sunDeep grid place-items-center mb-5">
                <Baby size={22} />
              </div>
              <h3 className="font-heading text-2xl mb-2">1:1 Sessions</h3>
              <p className="text-sm text-brand-muted font-body leading-relaxed">
                For children's sessions and parent coaching — private one-to-one work with Nada.
              </p>
              <Link
                data-testid="services-contact-1on1"
                to="/contact"
                className="mt-7 inline-flex items-center gap-2 rounded-full bg-brand-ink hover:bg-brand-leafDeep text-brand-cream px-6 py-3 text-sm font-medium transition-colors shadow-earthy"
              >
                Contact <ArrowRight size={14} />
              </Link>
            </div>

            <div className="bg-white rounded-[28px] border-2 border-brand-line hover:border-brand-sun/50 p-8 transition-colors">
              <div className="h-12 w-12 rounded-2xl bg-brand-leaf/20 text-brand-leafDeep grid place-items-center mb-5">
                <Users size={22} />
              </div>
              <h3 className="font-heading text-2xl mb-2">Group Classes</h3>
              <p className="text-sm text-brand-muted font-body leading-relaxed">
                Small online group sessions for peer connection, social skills, and shared regulation work.
              </p>
              <Link
                data-testid="services-contact-group"
                to="/contact"
                className="mt-7 inline-flex items-center gap-2 rounded-full bg-brand-ink hover:bg-brand-leafDeep text-brand-cream px-6 py-3 text-sm font-medium transition-colors shadow-earthy"
              >
                Contact <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
